import type { GetStaticPaths, GetStaticProps } from 'next'
import { isNil } from 'ramda'

import type {
  ArticleListResponse,
  CategoryListResponseDataItemCustom,
  NavigationResponse,
} from '@twisto/api-cms'

import { Navigation } from '../../components/navigation'
import {
  getCategories,
  getCategory,
  getNavigation,
  getPopularArticles,
} from '../../utils/api'
import { transformPaths } from '../../utils/paths'
import { CategoryOverview, MainCategoryOverview } from '../../views/'

type StaticProps = {
  navigation?: NavigationResponse
  category?: CategoryListResponseDataItemCustom['attributes']
  pageContext: { path?: string | string[] }
  popularArticles?: ArticleListResponse
}

const DynamicPage = ({
  navigation,
  category,
  popularArticles,
}: StaticProps) => {
  const isMainCategory = isNil(category?.parent)
  const navigationData = navigation?.data

  if (isNil(category)) {
    return {
      notFound: true,
    }
  }

  return (
    <>
      {!isNil(navigationData) && <Navigation {...navigationData} />}
      {category.categories && (
        <>
          {isMainCategory ? (
            <MainCategoryOverview
              category={category}
              popularArticles={popularArticles}
            />
          ) : (
            <CategoryOverview {...category} />
          )}
        </>
      )}
    </>
  )
}

export const getStaticPaths = (async () =>
  transformPaths(await getCategories())) satisfies GetStaticPaths

export const getStaticProps = (async (context) => {
  const { params } = context
  const url = [params?.path ?? ''].flat().join('/')
  const pageData = await getCategory(`/category/${url}`)

  if (isNil(pageData.data)) {
    return {
      notFound: true,
    }
  }

  const attributes = pageData.data
  const navigationId = attributes.navigation?.id ?? null
  const popularArticles = await getPopularArticles(pageData.data.id)

  const navigation = !isNil(navigationId)
    ? await getNavigation(navigationId)
    : undefined

  return {
    props: {
      category: attributes,
      navigation: navigation ?? null,
      popularArticles,
      pageContext: {
        path: attributes.path ?? null,
      },
    },

    revalidate: 1000,
  }
}) satisfies GetStaticProps

export default DynamicPage
